/**
 * AI Service Utility
 * Handles API calls to various AI providers (OpenAI, Gemini, Perplexity, etc.)
 */

export const callAI = async (provider, prompt, credentials) => {
    switch (provider) {
        case 'openai':
            return await callOpenAI(prompt, credentials.openai_key);
        case 'gemini':
            return await callGemini(prompt, credentials.gemini_key);
        case 'vertex':
            return await callVertex(prompt, credentials);
        case 'perplexity':
            return await callPerplexity(prompt, credentials.perplexity_key);
        case 'grok':
            return await callGrok(prompt, credentials.grok_key);
        case 'full_article':
            return await callFullArticleGen(prompt, credentials);
        case 'research_brief':
            return await callResearchBrief(prompt, credentials);
        default:
            throw new Error(`Provider ${provider} not supported or not configured.`);
    }
};

const callExhaustiveChain = async (type, prompt, credentials, prioritizeVertex = false) => {
    let providers = [
        { id: 'openai', key: credentials.openai_key, call: (p, k) => callOpenAI(p, k) },
        { id: 'vertex', key: credentials.vertex_credentials_json, call: (p, k) => callVertex(p, credentials) },
        { id: 'grok', key: credentials.grok_key, call: (p, k) => callGrok(p, k) },
        { id: 'gemini', key: credentials.gemini_key, call: (p, k) => callGemini(p, k) },
        { id: 'perplexity', key: credentials.perplexity_key, call: (p, k) => callPerplexity(p, k) }
    ];

    // Rearrange array to put Vertex first if requested (for heavy article lifting)
    if (prioritizeVertex) {
        const vertexIdx = providers.findIndex(p => p.id === 'vertex');
        if (vertexIdx > -1) {
            const vertexProv = providers.splice(vertexIdx, 1)[0];
            providers.unshift(vertexProv);
        }
    }

    const finalPrompt = type === 'research_brief' ? `
        ### RESEARCH MISSION
        Task: Extract concise, data-backed insights for the following request.
        Request: ${prompt}
        
        ### REQUIREMENTS:
        1. DATA-FIRST: Identify at least 3 specific numbers, percentages, or years.
        2. TRENDS: Briefly describe the "Market Shift" or "Business Impact".
        3. GLOBAL CONTEXT: Briefly relate this to US, AU, NZ, CA, SG, or PH markets.
        4. BREVITY: Max 200 words. Bullet points preferred. No conversational filler.
    ` : prompt;

    let lastError = null;

    for (const provider of providers) {
        if (provider.key) {
            try {
                console.log(`AI Service: Attempting ${type} with ${provider.id}...`);
                return await provider.call(finalPrompt, provider.key);
            } catch (err) {
                console.warn(`AI Service: ${provider.id} failed, moving to next...`, err.message);
                lastError = err;
                // If it's a quota issue or key issue, we definitely want to skip to next
                continue;
            }
        }
    }

    throw new Error(`AI_CHAIN_FAILED: All available AI providers failed. Last error: ${lastError?.message || 'No providers available'}`);
};

const callResearchBrief = async (prompt, credentials) => {
    return await callExhaustiveChain('research_brief', prompt, credentials);
};

const callFullArticleGen = async (prompt, credentials) => {
    // Stage 1: Generate Strategic Outline
    console.log("AI Service: Multi-Stage Stage 1 - Generating Strategic Outline (Vertex Priority)...");
    const outlinePrompt = `
        ### TASK: Generate a 7-8 Section Strategic Outline for a High-Authority Article (Target: 2,500 words).
        Topic: ${prompt}
        
        ### REQUIREMENTS:
        - Use "SEO + SGE + EEAT Optimized Publishing Framework (2026)" structure.
        - Return ONLY a JSON array of strings representing the section titles.
        - The structure must accommodate an in-depth, 2,500-word strategic discussion. 
        - Required Elements to include across sections:
          1. Introduction (Must include a Direct Answer Paragraph for AI Overviews)
          2. Section 1 \u2013 What Happened (Factual Data Summary)
          3. Section 2 \u2013 Strategic Commentary (Expert Layer / Differentiator)
          4. Section \u2013 Executive Implications (Should business owners act now?)
          5. Include placeholders for 1 Custom Data Visual and internal/external links in your mindset when structuring.
          6. FAQ Section (Minimum 4 Questions)
    `;

    let sections = [];
    try {
        const outlineResponse = await callExhaustiveChain('prompt', outlinePrompt, credentials, true);
        // Extract JSON if it exists in the response
        const jsonMatch = outlineResponse.match(/\[.*\]/s);
        if (jsonMatch) {
            sections = JSON.parse(jsonMatch[0]);
        } else {
            // Fallback to split lines if JSON fails
            sections = outlineResponse.split('\n').filter(l => l.trim() && l.length > 5).slice(0, 6);
        }
    } catch (err) {
        console.warn("Outline generation failed, using default structure:", err.message);
        sections = [
            "Introduction and Market Landscape",
            "The 2026 Shift: Comprehensive Data Summary",
            "Strategic Commentary: The Expert Interpretation",
            "Executive Implications and Actionable Insights",
            "Industry Benchmarks and Market Readiness",
            "Conclusion and Strategic Outlook"
        ];
    }

    // Stage 2: Sectional Drafting
    let fullHtml = "";
    for (let i = 0; i < sections.length; i++) {
        console.log(`AI Service: Multi-Stage Stage 2 - Drafting Section ${i + 1}/${sections.length}: ${sections[i]}...`);
        const sectionPrompt = `
            ### SECTIONAL DRAFTING TASK (${i + 1} of ${sections.length})
            Section Title: ${sections[i]}
            Target Topic: ${prompt}
            Previous Content Context: ${fullHtml.slice(-1500)} // Context for continuity
            
            ### CRITICAL REQUIREMENTS:
            - Write MINIMUM 400-500 words for this section to ensure the full article hits 2,500 words.
            - Follow EDITORIAL SOP (2026) rules exactly.
            - TONE: Business professional, not academic journal. Highly engaging, human-like insight. Avoid robotic, dense academic sentence structures.
            - FORMATTING: Short paragraphs (2\u20134 lines max). Use bullet points for clarity. Bold key insights.
            - OUTPUT RAW HTML ONLY (no markdown, no blocks).
            - Use <h2> for the section title and <h3> for subpoints.
            - Be analytical, authoritative, and data-dense. Reference geography (US, AU, UK, PH) if applicable.
        `;

        try {
            const sectionContent = await callExhaustiveChain('prompt', sectionPrompt, credentials, true);
            fullHtml += sectionContent + "\n";
        } catch (err) {
            console.error(`Section ${i + 1} failed:`, err.message);
            throw new Error(`Failed to draft section '${sections[i]}'. API Error: ${err.message}`);
        }
    }

    // Stage 3: FAQ Generation
    console.log("AI Service: Multi-Stage Stage 3 - Generating 4+ FAQ block...");
    const faqPrompt = `
        ### TASK: Generate a 4-Question FAQ Section
        Topic: ${prompt}
        Context: ${fullHtml.slice(0, 1000)}
        
        ### REQUIREMENTS:
        - Minimum 4 questions.
        - Answers must be 60-120 words each.
        - RAW HTML ONLY.
    `;

    try {
        const faqContent = await callExhaustiveChain('prompt', faqPrompt, credentials);
        fullHtml += `<section class="faq-section mt-16 pt-8 border-t border-gray-100"><h2>Industry FAQ</h2>${faqContent}</section>`;
    } catch (err) {
        console.warn("FAQ generation failed:", err.message);
    }

    return fullHtml;
};

export const searchImage = async (query, apiKey) => {
    if (!apiKey) return null;
    try {
        const response = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=1&orientation=landscape`, {
            headers: { 'Authorization': `Client-ID ${apiKey}` }
        });
        const data = await response.json();
        return data.results?.[0]?.urls?.regular || null;
    } catch (err) {
        console.error("Unsplash Error:", err);
        return null;
    }
};

export const generateImage = async (prompt, apiKey) => {
    if (!apiKey) return null;
    try {
        const response = await fetch('https://api.openai.com/v1/images/generations', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({
                model: 'dall-e-3',
                prompt: prompt,
                n: 1,
                size: '1024x1024'
            })
        });
        const data = await response.json();
        if (data.error) throw new Error(data.error.message);
        return data.data[0].url;
    } catch (err) {
        console.error("DALL-E Error:", err);
        return null;
    }
};

const callOpenAI = async (prompt, apiKey) => {
    try {
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({
                model: 'gpt-4o',
                messages: [{ role: 'user', content: prompt }],
                temperature: 0.7,
                max_tokens: 4096
            })
        });
        const data = await response.json();
        if (data.error) {
            const err = new Error(data.error.message || 'OpenAI API Error');
            if (data.error.code === 'insufficient_quota' || data.error.type === 'insufficient_quota') {
                err.isQuotaLimit = true;
            }
            err.provider = 'openai';
            throw err;
        }
        return data.choices[0].message.content;
    } catch (err) {
        if (!err.provider) err.provider = 'openai';
        throw err;
    }
};

const callGemini = async (prompt, apiKey) => {
    // Standardize probe: Try different model versions and names to find what the current API key supports
    const models = [
        { ver: 'v1beta', name: 'gemini-3.1-pro' },
        { ver: 'v1beta', name: 'gemini-1.5-flash' },
        { ver: 'v1beta', name: 'gemini-1.5-pro' },
        { ver: 'v1beta', name: 'gemini-2.0-flash-exp' },
        { ver: 'v1', name: 'gemini-1.5-flash' },
        { ver: 'v1', name: 'gemini-1.5-pro' }
    ];

    let lastError = null;
    let specificCause = "";
    let isQuota = false;

    for (const model of models) {
        try {
            console.log(`AI Service: Probing Gemini ${model.ver} with ${model.name}...`);
            const response = await fetch(`https://generativelanguage.googleapis.com/${model.ver}/models/${model.name}:generateContent?key=${apiKey}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contents: [{ parts: [{ text: prompt }] }]
                })
            });

            const data = await response.json();

            if (data.error) {
                // Track specific failure reasons for the final error report
                if (data.error.status === 'RESOURCE_EXHAUSTED') {
                    specificCause = "Quota exceeded (RESOURCE_EXHAUSTED). ";
                    isQuota = true;
                }
                if (data.error.status === 'PERMISSION_DENIED') specificCause = "Invalid API Key or Permission Denied. ";

                console.warn(`AI Service: Gemini probe failed for ${model.name} (${model.ver}):`, data.error.message);
                lastError = data.error.message;
                continue;
            }

            if (data.candidates && data.candidates.length > 0) {
                console.log(`AI Service: SUCCESS with ${model.name} on ${model.ver}`);
                return data.candidates[0].content.parts[0].text;
            }
        } catch (err) {
            console.error(`AI Service: Request error for ${model.name}:`, err.message);
            lastError = err.message;
        }
    }

    const err = new Error(`AI_LIMIT_REACHED: Gemini fallback failed after exhaustive probe. ${specificCause}Last error: ${lastError}\n\nPlease check your Google AI Studio credentials and ensure your quota hasn't been hit.`);
    err.isQuotaLimit = isQuota;
    err.provider = 'gemini';
    throw err;
};

export const testAIConnection = async (provider, credentials) => {
    const testPrompt = "Respond with only one word: 'OK'";
    try {
        switch (provider) {
            case 'openai':
                await callOpenAI(testPrompt, credentials.openai_key);
                break;
            case 'gemini':
                await callGemini(testPrompt, credentials.gemini_key);
                break;
            case 'vertex':
                await callVertex(testPrompt, credentials);
                break;
            case 'perplexity':
                await callPerplexity(testPrompt, credentials.perplexity_key);
                break;
            case 'grok':
                await callGrok(testPrompt, credentials.grok_key);
                break;
            default:
                return { status: 'error', message: 'Provider not supported' };
        }
        return { status: 'success', message: 'Connected' };
    } catch (err) {
        return {
            status: err.isQuotaLimit ? 'limit' : 'error',
            message: err.message,
            provider: err.provider || provider
        };
    }
};

const callVertex = async (prompt, credentials) => {
    try {
        console.log(`AI Service: Attempting Vertex AI (Gemini Pro)...`);

        const response = await fetch('/api/vertex', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                project_id: credentials.vertex_project_id,
                location: credentials.vertex_location,
                credentials_json: credentials.vertex_credentials_json,
                payload: {
                    model: 'gemini-1.5-pro',
                    data: {
                        contents: [{ parts: [{ text: prompt }] }]
                    }
                }
            })
        });

        if (!response.ok) {
            const text = await response.text();
            let data = {};
            try { data = JSON.parse(text); } catch (e) { }

            const errMessage = data.error?.error?.message || data.error?.message || `Vertex Error: ${response.status} ${response.statusText}`;
            console.warn(`AI Service: Vertex failed:`, errMessage);

            const statusToCheck = data.originalStatus || response.status;
            let isQuota = false;

            // Vertex / Google Auth specific quota checks
            if (statusToCheck === 429) isQuota = true;

            const err = new Error(errMessage);
            err.isQuotaLimit = isQuota;
            err.provider = 'vertex';
            throw err;
        }

        const data = await response.json();

        if (data.candidates && data.candidates.length > 0) {
            return data.candidates[0].content.parts[0].text;
        }

        throw new Error('Vertex API returned invalid response format.');

    } catch (err) {
        if (err.message === 'Failed to fetch') {
            const corsErr = new Error("Connection Failed. Make sure your local server is running, or verify Vercel function deployment.");
            corsErr.provider = 'vertex';
            throw corsErr;
        }
        if (!err.provider) err.provider = 'vertex';
        throw err;
    }
};

const callPerplexity = async (prompt, apiKey) => {
    try {
        const response = await fetch('/api/perplexity', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                apiKey: apiKey,
                payload: {
                    model: 'sonar',
                    messages: [{ role: 'user', content: prompt }]
                }
            })
        });

        // Handle non-200 responses if they don't return JSON
        if (!response.ok) {
            const text = await response.text();
            let data = {};
            try { data = JSON.parse(text); } catch (e) { }

            // Extract nested error if proxy returned it
            const errMessage = data.error?.error?.message || data.error?.message || `Perplexity Error: ${response.status} ${response.statusText}`;
            const err = new Error(errMessage);
            const statusToCheck = data.originalStatus || response.status;

            if (statusToCheck === 429 || text.includes('quota') || text.includes('balance')) {
                err.isQuotaLimit = true;
            }
            err.provider = 'perplexity';
            throw err;
        }

        const data = await response.json();
        if (data.error) {
            const err = new Error(data.error.message || 'Perplexity API Error');
            err.provider = 'perplexity';
            throw err;
        }
        return data.choices[0].message.content;
    } catch (err) {
        if (err.message === 'Failed to fetch') {
            const corsErr = new Error("Connection Failed. Make sure your local server is running, or verify Vercel function deployment.");
            corsErr.provider = 'perplexity';
            throw corsErr;
        }
        if (!err.provider) err.provider = 'perplexity';
        throw err;
    }
};

const callGrok = async (prompt, apiKey) => {
    // Try stable model names if latest fails
    const models = ['grok-2-latest', 'grok-beta', 'grok-2'];
    let lastError = null;
    let isQuota = false;

    for (const model of models) {
        try {
            console.log(`AI Service: Attempting Grok (${model})...`);
            const response = await fetch('/api/grok', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    apiKey: apiKey,
                    payload: {
                        model: model,
                        messages: [
                            { role: 'system', content: 'You are Grok, a helpful and witty AI assistant by xAI.' },
                            { role: 'user', content: prompt }
                        ],
                        stream: false,
                        temperature: 0.7
                    }
                })
            });

            // If it's a 4xx code but not JSON, or other non-OK status
            if (!response.ok) {
                const text = await response.text();
                let data = {};
                try { data = JSON.parse(text); } catch (e) { }

                const errMessage = data.error?.error?.message || data.error?.message || `Grok Error: ${response.status} ${response.statusText}`;
                console.warn(`AI Service: Grok ${model} failed:`, errMessage);

                const statusToCheck = data.originalStatus || response.status;
                if (statusToCheck === 429) isQuota = true;
                lastError = errMessage;
                continue; // Try next model
            }

            const data = await response.json();
            if (data.error) {
                console.warn(`AI Service: Grok ${model} API error:`, data.error.message);
                lastError = data.error.message;
                continue;
            }

            if (data.choices && data.choices.length > 0) {
                return data.choices[0].message.content;
            }
        } catch (err) {
            if (err.message === 'Failed to fetch') {
                const corsErr = new Error("Connection Failed. Make sure your local server is running, or verify Vercel function deployment.");
                corsErr.provider = 'grok';
                throw corsErr;
            }
            console.error(`AI Service: Grok ${model} request error:`, err.message);
            lastError = err.message;
        }
    }

    const finalErr = new Error(`Grok failed after probing models. Last error: ${lastError || 'Unknown Error'}`);
    finalErr.isQuotaLimit = isQuota;
    finalErr.provider = 'grok';
    throw finalErr;
};
